/**
 * 版本号管理工具
 * 自动从package.json读取版本号，确保版本号的一致性
 */

// 从package.json中读取版本号
const getVersionFromPackage = (): string => {
  try {
    // 优先使用环境变量
    if (process.env.REACT_APP_VERSION) {
      return process.env.REACT_APP_VERSION;
    }

    // 如果环境变量不存在，使用硬编码的当前版本
    // 这个值需要在每次发版时手动更新
    return '1.2.3';
  } catch (error) {
    console.warn('无法读取版本号，使用默认版本', error);
    return '1.2.3';
  }
};

// 获取版本状态
const getVersionStatus = (version: string): string => {
  const [major, minor, patch] = version.split('.').map(Number);

  // 根据版本号判断状态
  if (major >= 2) {
    return 'PRODUCTION';
  } else if (major === 1 && minor >= 1) {
    return 'BETA';
  } else if (major === 1 && minor === 0 && patch >= 2) {
    return 'STABLE';
  } else {
    return 'ALPHA';
  }
};

// 获取版本状态颜色
const getVersionStatusColor = (
  status: string
): { bg: string; text: string; border: string } => {
  switch (status) {
    case 'PRODUCTION':
      return {
        bg: 'rgba(34, 197, 94, 0.08)',
        text: '#16a34a',
        border: 'rgba(34, 197, 94, 0.15)',
      };
    case 'BETA':
      return {
        bg: 'rgba(59, 130, 246, 0.08)',
        text: '#2563eb',
        border: 'rgba(59, 130, 246, 0.15)',
      };
    case 'STABLE':
      return {
        bg: 'rgba(16, 185, 129, 0.08)',
        text: '#059669',
        border: 'rgba(16, 185, 129, 0.15)',
      };
    case 'ALPHA':
      return {
        bg: 'rgba(245, 158, 11, 0.08)',
        text: '#d97706',
        border: 'rgba(245, 158, 11, 0.15)',
      };
    default:
      return {
        bg: 'rgba(107, 114, 128, 0.08)',
        text: '#6b7280',
        border: 'rgba(107, 114, 128, 0.15)',
      };
  }
};

// 获取版本描述 - 返回英文，组件内可通过 i18n 覆盖
const getVersionDescription = (status: string): string => {
  switch (status) {
    case 'PRODUCTION':
      return 'Production';
    case 'BETA':
      return 'Beta';
    case 'STABLE':
      return 'Stable';
    case 'ALPHA':
      return 'Alpha';
    default:
      return 'Dev';
  }
};

// 导出版本信息对象
export const VersionInfo = {
  // 获取当前版本号
  get version(): string {
    return getVersionFromPackage();
  },

  // 获取版本状态
  get status(): string {
    return getVersionStatus(this.version);
  },

  // 获取版本状态颜色
  get statusColors(): { bg: string; text: string; border: string } {
    return getVersionStatusColor(this.status);
  },

  // 获取版本描述
  get description(): string {
    return getVersionDescription(this.status);
  },

  // 获取完整版本信息
  get fullInfo(): {
    version: string;
    status: string;
    description: string;
    colors: { bg: string; text: string; border: string };
  } {
    return {
      version: this.version,
      status: this.status,
      description: this.description,
      colors: this.statusColors,
    };
  },

  // 获取格式化的版本显示文本
  get displayVersion(): string {
    return `v${this.version}`;
  },

  // 获取版权信息（默认英文，组件内应使用 t('common:app.copyright') 覆盖）
  get copyright(): string {
    const currentYear = new Date().getFullYear();
    return `© ${currentYear} Offshore Green Bond Express`;
  },
};

export default VersionInfo;
